import { importRoster, rosterError } from "./api";
import { ImportCsvModal } from "./ImportCsvModal";
import { ageCell, cardSummary, rosterCsvHeader, type CardKey } from "./roster";
import type { RosterImportResult, RosterImportRow } from "./types";
import styles from "./SourceTab.module.css";

/**
 * Import a roster from a CSV — ImportCsvModal, told which table it is looking
 * at.
 *
 * What is here is the whole of what a roster import is not a guild-template
 * import: the noun, the endpoint, the header this project's card makes, and
 * the six columns of the preview. Everything else — the preview round trip,
 * the grouped refusals, the drop zone, the 2 MB guard — is the one dialog both
 * tables share.
 *
 * Mockups: t-103/bots-source.html §4 and t-117/bots-account-age.html §4.
 */

interface Props {
  projectID: string;
  cardKeys: CardKey[];
  onClose: () => void;
  /** The real import landed — the tab toasts it and refetches. */
  onDone: (result: RosterImportResult) => void;
}

export function ImportRosterModal({
  projectID,
  cardKeys,
  onClose,
  onDone,
}: Props) {
  return (
    <ImportCsvModal<RosterImportRow, RosterImportResult>
      title="Import bots from CSV"
      nouns={{ one: "row", many: "rows" }}
      dropTitle="Drop a roster CSV here"
      fileLabel="Roster CSV"
      header={rosterCsvHeader(cardKeys)}
      modeNote="A row that already became a bot is never touched by an import — that bot exists, and a table cannot un-create it."
      refusedFileName="roster-refused-lines.csv"
      run={(input) => importRoster(projectID, input)}
      errorOf={rosterError}
      previewHead={
        <>
          {/* The FILE's line, which is what the refusals above this table are
              named by and what the operator has open in a spreadsheet. Where
              the row will sit is «spent top to bottom» and is answered by the
              table itself. */}
          <th className={styles.num} scope="col">
            Line
          </th>
          <th scope="col">Name</th>
          {/* Second, as it is everywhere else a row is read — and it has to be
              read HERE in particular: the header printed above this table
              already names `avatar`, so a preview without the column would
              show a file whose second column had gone missing. This is also
              the last screen before the rows are written, which is where an
              operator checks a batch they set the avatars of. */}
          <th scope="col">Avatar</th>
          <th scope="col">Country</th>
          <th scope="col">Behaviour</th>
          {/* Where it is in the table, in the row editor and in the file's own
              header: after Behaviour, before the card. A line that gave no age
              shows the same dash the table shows, and means the same thing —
              this row will be given one from `bots.account_age`. */}
          <th className={styles.num} scope="col">
            Account age
          </th>
          <th scope="col">Card</th>
        </>
      }
      previewRow={(row, i) => (
        <tr key={`${row.line}-${i}`}>
          <td className={styles.num}>{row.line}</td>
          <td>{row.display_name}</td>
          {/* Not marked against the whitelist, unlike the table's own cell: the
              preview describes the FILE, and nothing has been written yet. The
              row that lands is marked in the table a moment later. */}
          <td className={styles.mono}>{row.avatar ?? "—"}</td>
          <td className={styles.mono}>{row.country ?? "—"}</td>
          <td className={styles.mono}>{row.profile_key ?? "—"}</td>
          <td className={styles.num}>{ageCell(row.age_days)}</td>
          <td className={styles.cardCell}>
            {cardSummary(row.stats, row.meta, cardKeys) || "—"}
          </td>
        </tr>
      )}
      onClose={onClose}
      onDone={onDone}
    />
  );
}
